import { useEffect } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { onboardingApi } from '@/api/onboarding'
import { settingsApi } from '@/api/settings'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { isLearningDemoCompleted } from '@/lib/learningDemoStorage'
import { useAuthStore } from '@/store/authStore'
import { useSettingsStore } from '@/store/settingsStore'

export function OnboardingGuard() {
  const location = useLocation()
  const onWelcome = location.pathname === '/welcome'
  const onLearningDemo = location.pathname === '/learning-demo'
  const replayRequested =
    onLearningDemo && new URLSearchParams(location.search).get('replay') === '1'
  const userId = useAuthStore((state) => state.user?.id)
  const setSettings = useSettingsStore((state) => state.setSettings)
  const locallyDemoDone = userId ? isLearningDemoCompleted(userId) : false

  const settingsQuery = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.getSettings,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })

  const demoStatusQuery = useQuery({
    queryKey: ['learning-demo-status', userId],
    queryFn: onboardingApi.getLearningDemoStatus,
    enabled: Boolean(userId) && !locallyDemoDone,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (settingsQuery.data) {
      setSettings(settingsQuery.data)
    }
  }, [settingsQuery.data, setSettings])

  if (settingsQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (settingsQuery.isError) {
    return <Outlet />
  }

  const completed = settingsQuery.data?.onboardingCompleted ?? false

  if (completed && demoStatusQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!completed && !onWelcome) {
    return <Navigate to="/welcome" replace />
  }

  if (completed && onWelcome) {
    return <Navigate to="/learning-demo" replace />
  }

  const demoCompleted =
    demoStatusQuery.data?.completed === true ||
    (userId ? isLearningDemoCompleted(userId) : false)

  if (completed && !demoCompleted && !onLearningDemo) {
    return <Navigate to="/learning-demo" replace />
  }

  if (completed && demoCompleted && onLearningDemo && !replayRequested) {
    return <Navigate to="/dashboard" replace />
  }

  return <Outlet />
}
